<script>
import { REPORT_PALETTE } from '@modules/consolidator/constants/Color'
import { toPercent } from '@utils/Format'
import { customSort } from '@utils/Sort'
import { isOffshoreInvestmentType } from '@modules/consolidator/utils/InvestmentType/InvestmentType.js'
import { getWalletModoLabel } from '@modules/consolidator/utils/wallet-modo-configs/wallet-modo-configs'
import { ANBIMA_RISCO_COLOR } from '@modules/consolidator/constants/anbima-risco-color'
import { ANBIMA_STATUS } from '@modules/consolidator/constants/anbima-status'
import { buildDesenquadramentoRows } from '@modules/consolidator/utils/build-desenquadramento-data/build-desenquadramento-data'
import FeedbackWrapper from '@modules/global/components/feedback-wrapper/feedback-wrapper.vue'
import GeneralValueDiff from '@modules/global/components/general-value-diff'

export default {
  name: 'DetalhamentoAlocacaoEstrategica',
  components: {
    FeedbackWrapper,
    GeneralValueDiff,
  },
  props: {
    alocacaoClienteInvalida: {
      type: Boolean,
      default: false,
    },
    perfil: {
      type: String,
      default: '',
    },
    statusBalanceamento: {
      type: String,
      default: '',
    },
    temFundoComposto: {
      type: Boolean,
      default: false,
    },
    fundoCompostoExpandido: {
      type: Boolean,
      default: false,
    },
    filteredClassesWithPerfil: {
      type: Array,
      default: () => [],
    },
    classesComComposicao: {
      type: Array,
      default: () => [],
    },
    alocationDataEstatistics: {
      type: Object,
      default: () => ({}),
    },
    mapClassRent: {
      type: Object,
      default: () => ({}),
    },
    getClassVol: {
      type: Function,
      default: () => () => {},
    },
    groupBy: {
      type: String,
      default: '',
    },
    resultFracoes: {
      type: Object,
      default: () => ({}),
    },
    resultFracoesClasse: {
      type: Object,
      default: () => ({}),
    },
    mapperBenchmarks: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    contentOnly: {
      type: Boolean,
      default: false,
    },
    colorsByLabel: {
      type: Object,
      default: null,
    },
    investmentType: {
      type: String,
      default: '',
    },
    viewMode: {
      type: String,
      default: 'estrategica',
      validator: (v) => ['estrategica', 'desenquadramento'].includes(v),
    },
    fracoesAnbima: {
      type: Object,
      default: () => ({}),
    },
    maxAnbimaPorClasse: {
      type: Object,
      default: () => ({}),
    },
    statusDesenquadramento: {
      type: String,
      default: null,
    },
    clienteSemPerfil: {
      type: Boolean,
      default: false,
    },
    podeDefinirPerfil: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      SIGNAL_NEAR_COLOR: 'var(--warning-new)',
      FIELD_VALUE: 'value',
      STATUS_DESBALANCEADO: 'Desbalanceado',
      fundoCompostoExpandidoLocal: false,
      viewModeOptions: [
        { text: 'Estratégica', value: 'estrategica' },
        { text: 'Desenquadramento', value: 'desenquadramento' },
      ],
    }
  },
  computed: {
    isDesenquadramentoView() {
      return this.viewMode === 'desenquadramento'
    },
    anbimHeaders() {
      return [
        { text: '', value: 'color', sortable: false, width: '4px' },
        { text: 'Classe ANBIMA', value: 'classe_anbima' },
        { text: '% Atual', value: 'fracao' },
        { text: '% Máximo', value: 'maxPct' },
        { text: 'Diff', value: 'diff', align: 'center', sortable: false },
      ]
    },
    // Linhas da tabela (incluindo a agregada 'Outros'): a derivação inteira
    // vive no util, junto dos testes.
    desenquadramentoTableItems() {
      return buildDesenquadramentoRows(
        this.fracoesAnbima,
        this.maxAnbimaPorClasse
      )
    },
    // Status ANBIMA por nome, para o template não repetir a string literal —
    // ela é contrato com o backend e vive em ANBIMA_STATUS.
    isRiscoAcima() {
      return this.statusDesenquadramento === ANBIMA_STATUS.ACIMA
    },
    isRiscoDentro() {
      return this.statusDesenquadramento === ANBIMA_STATUS.DENTRO
    },
    isRiscoNaoConfigurado() {
      return this.statusDesenquadramento === ANBIMA_STATUS.NAO_CONFIGURADO
    },
    temVeredito() {
      return !!this.statusDesenquadramento
    },
    mostraConviteAnbima() {
      return this.isRiscoNaoConfigurado
    },
    mostraEstadoSemPerfil() {
      if (this.mostraConviteAnbima) return false
      return this.clienteSemPerfil
    },
    mostraCtaDefinirPerfil() {
      if (!this.mostraEstadoSemPerfil) return false
      return this.podeDefinirPerfil
    },
    mostraEstadoSemPosicao() {
      return !this.temVeredito || this.desenquadramentoTableItems.length === 0
    },
    mostraEstadoVazioDesenquadramento() {
      if (this.mostraConviteAnbima) return true
      if (this.mostraEstadoSemPerfil) return true
      return this.mostraEstadoSemPosicao
    },
    mostraStatusGeral() {
      return !this.mostraEstadoVazioDesenquadramento
    },
    mostraEstadoVazioEstrategica() {
      return this.tableItems.length === 0
    },
    estrategicaVaziaTexto() {
      return 'Não há posição consolidada para exibir a distribuição por classe. Se houver filtros aplicados, revise-os.'
    },
    isDesbalanceado() {
      return this.statusBalanceamento === this.STATUS_DESBALANCEADO
    },
    sortByValue() {
      return [this.FIELD_VALUE]
    },
    // 'Acima do Risco' / 'Dentro do Risco' já ganham cor pela classe
    // text-danger/text-success no template; aqui só os dois status que
    // precisam de cor fora dessa dupla (sem classe utilitária equivalente).
    // 'Não Configurado' usa o mesmo valor de ANBIMA_RISCO_COLOR (compartilhado
    // com consolidated-tables.vue) para não divergir entre as duas telas —
    // é a ÚNICA chave do mapa consumida como valor CSS direto aqui (as outras
    // 3 chaves são nomes de prop do BaseStatus, usadas só pela lista). Os
    // demais status desta tela vêm de outra fonte: 'Acima do Risco' e
    // 'Dentro do Risco' pela classe utilitária text-danger/text-success (no
    // template), 'Próximo do Risco' de SIGNAL_NEAR_COLOR.
    statusRiscoAnbimaStyle() {
      if (this.statusDesenquadramento === ANBIMA_STATUS.PROXIMO) {
        return { color: this.SIGNAL_NEAR_COLOR }
      }
      if (this.isRiscoNaoConfigurado) {
        return { color: ANBIMA_RISCO_COLOR[ANBIMA_STATUS.NAO_CONFIGURADO] }
      }
      return {}
    },
    hasNoData() {
      return this.isOffshore
    },
    noDataMessage() {
      if (this.isOffshore) {
        return 'Detalhamento não disponível para investimentos offshore'
      }
      return 'Sem dados para exibir no momento'
    },
    isOffshore() {
      return isOffshoreInvestmentType({ investmentType: this.investmentType })
    },
    showStatisticsGroup() {
      return ['classe', 'subclasse', 'corretora', 'nome_portfolio'].includes(
        this.groupBy
      )
    },
    showSuggestedColumns() {
      return ['classe', 'subclasse'].includes(this.groupBy)
    },
    fractions() {
      if (this.groupBy === 'classe') {
        return this.resultFracoesClasse
      }
      return this.resultFracoes
    },
    groupDisplayName() {
      return getWalletModoLabel(this.groupBy) || this.groupBy
    },
    tableHeaders() {
      const headers = [
        {
          text: '',
          value: 'color',
          sortable: false,
        },
        {
          text: this.groupDisplayName,
          value: 'name',
        },
        {
          text: '% P/L atual',
          value: this.FIELD_VALUE,
        },
      ]

      if (this.fractions && this.perfil && this.showSuggestedColumns) {
        headers.push(
          {
            text: '% Sugerido',
            value: 'composicao_perfil',
          },
          {
            text: 'Diff',
            value: 'diff',
            align: 'center',
            sortable: false,
          }
        )
      }

      headers.push(
        {
          text: 'Rent.',
          value: 'rentabilidade',
        },
        {
          text: 'Vol.',
          value: 'vol',
        },
        {
          text: 'Rent. Relativa',
          value: 'rent_relativa',
          sortable: false,
        },
        {
          text: 'Vol. Relativa',
          value: 'vol_relativa',
          sortable: false,
        }
      )

      return headers
    },
    tableItems() {
      const itemsWithColor = [...this.filteredClassesWithPerfil]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((item, index) => ({
          ...item,
          color:
            (this.colorsByLabel && this.colorsByLabel[item.name]) ||
            REPORT_PALETTE[index % REPORT_PALETTE.length],
        }))

      const sortedItems = itemsWithColor.sort((a, b) => b.value - a.value)

      return sortedItems.map((classe) => ({
        ...classe,
        rentabilidade: (this.alocationDataEstatistics[classe.name] || {})
          .inicio,
        vol: (this.alocationDataEstatistics[classe.name] || {}).vol,
        rent_relativa: this.showStatisticsGroup
          ? this.mapClassRent[classe.name]
          : '-',
        vol_relativa: this.showStatisticsGroup
          ? this.getClassVol(classe.name, this.mapperBenchmarks[classe.name])
          : '-',
        hasComposition:
          this.classesComComposicao.includes(classe.name) &&
          this.fundoCompostoExpandido,
        signalClass: this.isDesbalanceado ? 'red' : 'secondary',
      }))
    },
  },
  methods: {
    decToPercent(v, n = 2) {
      return toPercent(v * 100, n)
    },
    formatPctMaybeNull(v, n = 1) {
      if (typeof v !== 'number') {
        return '—'
      }
      return toPercent(v * 100, n)
    },
    onChangeViewMode(value) {
      this.$emit('change-view-mode', value)
    },
    onDefinirPerfil() {
      this.$emit('definir-perfil')
    },
    podeConfigurarAnbima() {
      const user = this.$currentUser()
      return !user.IS_CLIENTE && !!user.IS_GESTOR
    },
    semPosicaoTexto() {
      if (this.$currentUser().IS_CLIENTE) {
        return 'Não há posição consolidada para comparar com os limites por classe ANBIMA. Revise os filtros aplicados; se a carteira já tem posições, o enquadramento aparece assim que o perfil de investidor estiver definido.'
      }
      return 'Não há posição consolidada para comparar com os limites por classe ANBIMA. Se houver filtros aplicados, revise-os.'
    },
    conviteAnbimaTexto() {
      if (this.podeConfigurarAnbima()) {
        return 'Defina os limites por classe ANBIMA para acompanhar o desenquadramento deste perfil.'
      }
      if (this.$currentUser().IS_CLIENTE) {
        return 'Os limites por classe ANBIMA deste perfil ainda não foram definidos.'
      }
      return 'Peça ao gestor do escritório para definir os máximos ANBIMA em Configurações, na aba Perfis.'
    },
    irParaConfiguracaoMaximosAnbima() {
      const perfilParaConfigurar = String(this.perfil || '').trim()
      if (!perfilParaConfigurar) {
        this.$router.push({ name: 'settings.portfolio' })
        return
      }
      this.$router.push({
        name: 'settings.portfolio',
        query: { perfil: perfilParaConfigurar },
      })
    },
    toggleFundoCompostoExpandido() {
      this.fundoCompostoExpandidoLocal = !this.fundoCompostoExpandidoLocal
      this.salvarFundoCompostoConfigLocal(this.fundoCompostoExpandidoLocal)
    },
    salvarFundoCompostoConfigLocal($event) {
      this.$emit('salvarFundoCompostoConfigLocal', $event)
      this.$emit('changeFundoCompostoExpandido', $event)
    },
    customSort(a, b, c) {
      const dict = {
        texts: ['name'],
        dates: [],
        numbers: [
          this.FIELD_VALUE,
          'composicao_perfil',
          'rentabilidade',
          'vol',
        ],
      }
      return customSort(a, b, c, dict)
    },
    getDiffIcon(signal) {
      if (signal === '<') return 'mdi-menu-down-outline'
      if (signal === '>') return 'mdi-menu-up-outline'
      return 'mdi-minus'
    },
  },
}
</script>

<template>
  <div
    class="detalhamento-alocacao-estrategica"
    :class="{
      'az-card-bordered': !contentOnly,
      'detalhamento-alocacao-estrategica--loading': loading,
      'detalhamento-alocacao-estrategica--content-only': contentOnly,
    }"
  >
    <FeedbackWrapper
      :loading="contentOnly ? false : loading"
      :noData="hasNoData"
      :message="noDataMessage"
      :minHeight="contentOnly ? 0 : 425"
      :class="{ 'rounded-sm': !contentOnly }"
    >
      <div class="detalhamento-alocacao-estrategica__header">
        <div
          v-if="!contentOnly"
          class="detalhamento-alocacao-estrategica__title az-ds-card-title"
        >
          Detalhamento Alocação Estratégica
        </div>

        <BaseToggleButton
          :value="viewMode || 'estrategica'"
          :options="viewModeOptions"
          class="detalhamento-alocacao-estrategica__toggle"
          @input="onChangeViewMode"
        />

        <div
          class="detalhamento-alocacao-estrategica__info az-text-primary az-size-12 font-semibold az-muted"
        >
          <div class="detalhamento-alocacao-estrategica__info-perfil">
            <span> Perfil cliente: </span>
            <span
              :class="{
                'text-danger':
                  !$currentUser().IS_CLIENTE && alocacaoClienteInvalida,
              }"
            >
              {{ perfil || '-' }}
            </span>

            <v-tooltip
              v-if="!$currentUser().IS_CLIENTE && alocacaoClienteInvalida"
              top
            >
              <template v-slot:activator="{ on, attrs }">
                <icon-svg
                  name="mdi-information"
                  size="16"
                  color="red"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <span>
                Para acompanhar a aderência, o perfil precisa estar detalhado
                por subclasse.
                <br />
                Ajuste em Configurações -> Perfis.
              </span>
            </v-tooltip>
          </div>

          <div
            v-if="fractions && $isWhitelabel()"
            class="detalhamento-alocacao-estrategica__info-status"
          >
            <span> Status: </span>
            <span
              :class="{
                'text-success': statusBalanceamento === 'Balanceado',
                'text-danger': isDesbalanceado,
              }"
            >
              {{ statusBalanceamento }}
            </span>
          </div>

          <div
            v-if="statusDesenquadramento"
            class="detalhamento-alocacao-estrategica__info-risco"
          >
            <span> Risco ANBIMA: </span>
            <span
              :class="{
                'text-success': isRiscoDentro,
                'text-danger': isRiscoAcima,
              }"
              :style="statusRiscoAnbimaStyle"
            >
              {{ statusDesenquadramento }}
            </span>
          </div>

          <div
            v-if="temFundoComposto && showStatisticsGroup"
            class="detalhamento-alocacao-estrategica__info-expandir"
          >
            <BaseButton
              secondary
              text
              sm
              fit-content
              @click="toggleFundoCompostoExpandido"
            >
              <icon-svg
                :name="
                  fundoCompostoExpandidoLocal
                    ? 'mdi-checkbox-marked'
                    : 'mdi-checkbox-blank-outline'
                "
                :size="16"
                color="primary"
              />
              Expandir ativos com composição
            </BaseButton>
          </div>
        </div>
      </div>

      <div
        v-if="isDesenquadramentoView"
        class="d-flex flex-column justify-content-between"
      >
        <FeedbackWrapper
          :noData="mostraEstadoVazioDesenquadramento"
          :minHeight="160"
        >
          <template v-slot:content>
            <div
              v-if="mostraConviteAnbima"
              data-testid="convite-configurar-anbima"
              class="text-center"
            >
              <p class="ma-0 az-text-primary az-size-14 font-semibold">
                Este perfil ainda não tem máximos ANBIMA definidos
              </p>
              <p class="ma-0 mt-1 az-text-primary az-size-12 az-muted">
                {{ conviteAnbimaTexto() }}
              </p>
              <div
                v-if="podeConfigurarAnbima()"
                class="d-flex justify-center mt-4"
              >
                <BaseButton
                  data-testid="cta-configurar-anbima"
                  primary
                  fit-content
                  min-width="auto"
                  @click="irParaConfiguracaoMaximosAnbima"
                >
                  Definir máximos ANBIMA
                </BaseButton>
              </div>
            </div>

            <div
              v-else-if="mostraEstadoSemPerfil"
              data-testid="estado-sem-perfil"
              class="text-center"
            >
              <p class="ma-0 az-text-primary az-size-14 font-semibold">
                Este cliente ainda não tem perfil de investidor
              </p>
              <p class="ma-0 mt-1 az-text-primary az-size-12 az-muted">
                Sem o perfil do investidor não há limites por classe ANBIMA para
                comparar com a carteira.
              </p>
              <div
                v-if="mostraCtaDefinirPerfil"
                class="d-flex justify-center mt-4"
              >
                <BaseButton
                  data-testid="cta-definir-perfil"
                  primary
                  fit-content
                  min-width="auto"
                  @click="onDefinirPerfil"
                >
                  Definir perfil
                </BaseButton>
              </div>
            </div>

            <div v-else data-testid="estado-sem-posicao" class="text-center">
              <p class="ma-0 az-text-primary az-size-14 font-semibold">
                Sem posição para comparar
              </p>
              <p class="ma-0 mt-1 az-text-primary az-size-12 az-muted">
                {{ semPosicaoTexto() }}
              </p>
            </div>
          </template>

          <v-data-table
            id="tabela-alocacao-anbima"
            class="detalhamento-alocacao-estrategica__table table-new-ux-v1"
            :headers="anbimHeaders"
            :items="desenquadramentoTableItems"
            :header-props="{ 'sort-icon': 'mdi-chevron-up' }"
            :sort-by="['fracao']"
            :sort-desc="[true]"
            dense
            hide-default-footer
            disable-pagination
          >
            <template v-slot:item.color="{ item }">
              <div :style="{ 'background-color': item.barColor }"></div>
            </template>

            <template v-slot:item.classe_anbima="{ item }">
              <span>{{ item.classe_anbima }}</span>
              <v-tooltip v-if="item.isOutrosGroup" right>
                <template v-slot:activator="{ on, attrs }">
                  <!-- Estrutura padrão de ação em tabela do design-system
                     (design-system/patterns.md, "Table action structure"):
                     v-tooltip > v-btn icon > icon-svg — a mesma já usada na
                     coluna Diff deste arquivo. O v-btn renderiza <button>
                     nativo, então o "+" (única porta para o detalhamento do
                     agrupamento) continua alcançável por teclado sem ARIA
                     extra, atendendo Web:S6819 e
                     MouseEventWithoutKeyboardEquivalentCheck do Sonar. -->
                  <v-btn
                    icon
                    x-small
                    class="ml-1"
                    aria-label="Ver classes somadas no teto de Outros"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <icon-svg name="plus" :size="16" color="#8E8E8E" />
                  </v-btn>
                </template>
                <div>
                  <!-- O título descreve o CONJUNTO listado, não só as classes sem
                     teto: a própria 'Outros' entra no pool (o backend marca
                     grouped_as_outros=True para ela também) e precisa continuar
                     listada, senão as parcelas visíveis somam menos que a
                     fração da linha. -->
                  <!-- `caption` (utility do Vuetify) e NÃO az-text-*: o
                     v-tooltip tem fundo escuro e as classes az-text-* forçam
                     cor escura, deixando o título ilegível. -->
                  <div class="caption font-semibold mb-1">
                    Somadas contra o teto de 'Outros':
                  </div>
                  <div
                    v-for="breakdown in item.outrosBreakdown"
                    :key="breakdown.classe"
                  >
                    {{ breakdown.classe }}:
                    {{ formatPctMaybeNull(breakdown.fracao) }}
                  </div>
                </div>
              </v-tooltip>
            </template>

            <template v-slot:item.fracao="{ item }">
              {{ formatPctMaybeNull(item.fracao) }}
            </template>

            <template v-slot:item.maxPct="{ item }">
              {{ formatPctMaybeNull(item.maxPct) }}
            </template>

            <template v-slot:item.diff="{ item }">
              <div v-if="item.isOverLimit && item.diff !== null">
                <v-tooltip right>
                  <template v-slot:activator="{ on }">
                    <v-btn small icon v-on="on">
                      <icon-svg
                        name="mdi-menu-up-outline"
                        size="20"
                        color="red"
                      />
                    </v-btn>
                  </template>
                  <span
                    >{{ formatPctMaybeNull(item.diff) }} acima do máximo</span
                  >
                </v-tooltip>
              </div>
              <div v-else-if="item.isNear">
                <v-tooltip right>
                  <template v-slot:activator="{ on }">
                    <v-btn small icon v-on="on">
                      <icon-svg
                        name="mdi-alert-outline"
                        size="20"
                        color="warning"
                      />
                    </v-btn>
                  </template>
                  <span
                    >Próximo ao limite — dentro de 10% relativo do máximo</span
                  >
                </v-tooltip>
              </div>
              <span v-else>—</span>
            </template>
          </v-data-table>
        </FeedbackWrapper>

        <small
          v-if="mostraStatusGeral"
          class="mt-3 az-text-primary az-size-12 font-semibold"
          :class="{
            'text-danger': isRiscoAcima,
            'text-success': isRiscoDentro,
          }"
          :style="statusRiscoAnbimaStyle"
        >
          Status geral: {{ statusDesenquadramento }}
        </small>
      </div>

      <div v-else class="d-flex flex-column justify-content-between">
        <FeedbackWrapper
          :noData="mostraEstadoVazioEstrategica"
          :minHeight="160"
        >
          <template v-slot:content>
            <div data-testid="estado-vazio-estrategica" class="text-center">
              <p class="ma-0 az-text-primary az-size-14 font-semibold">
                Sem posição para exibir
              </p>
              <p class="ma-0 mt-1 az-text-primary az-size-12 az-muted">
                {{ estrategicaVaziaTexto }}
              </p>
            </div>
          </template>

          <v-data-table
            id="tabela-alocacao-estrategica"
            class="detalhamento-alocacao-estrategica__table table-new-ux-v1"
            :headers="tableHeaders"
            :items="tableItems"
            :custom-sort="customSort"
            :header-props="{
              'sort-icon': 'mdi-chevron-up',
            }"
            :sort-by="sortByValue"
            :sort-desc="[true]"
            dense
            hide-default-footer
            disable-pagination
          >
            <template v-slot:item.color="{ item }">
              <div :style="{ 'background-color': item.color }"></div>
            </template>

            <template v-slot:item.name="{ item }">
              <div>
                {{ item.name }}
                <span v-if="item.hasComposition">*</span>
              </div>
            </template>

            <template v-slot:item.composicao_perfil="{ item }">
              <div>
                {{
                  item.composicao_perfil && showStatisticsGroup
                    ? decToPercent(item.composicao_perfil, 1)
                    : '-'
                }}
              </div>
            </template>

            <template v-slot:item.diff="{ item }">
              <div
                v-if="fractions && showStatisticsGroup && perfil && item.diff"
              >
                <v-tooltip
                  :class="{
                    [`icone-aviso-${item.signal}`]: true,
                    'icone-aviso-red': isDesbalanceado,
                  }"
                  right
                >
                  <template v-slot:activator="{ on }">
                    <v-btn small icon v-on="on">
                      <icon-svg
                        :name="getDiffIcon(item.signal)"
                        size="20"
                        :color="item.signalClass"
                      />
                    </v-btn>
                  </template>
                  <span>
                    {{ decToPercent(item.diff) }}
                    fora do sugerido
                  </span>
                </v-tooltip>
              </div>
            </template>

            <template v-slot:item.rentabilidade="{ item }">
              <GeneralValueDiff
                v-if="typeof item.rentabilidade === 'number'"
                :value="item.rentabilidade"
                :value-formatted="decToPercent(item.rentabilidade)"
                style="margin: 0 !important; font-size: 12px"
              />
              <span v-else> - </span>
            </template>

            <template v-slot:item.vol="{ item }">
              {{
                item.vol && showStatisticsGroup ? decToPercent(item.vol) : '-'
              }}
            </template>

            <template
              v-if="fundoCompostoExpandido && temFundoComposto"
              v-slot:body.append
            >
              <tr>
                <td :colspan="tableHeaders.length">
                  <small class="az-text-small">
                    * Parte da alocação da classe é da composição de um ativo.
                  </small>
                </td>
              </tr>
            </template>
          </v-data-table>
        </FeedbackWrapper>

        <small v-if="!showStatisticsGroup" class="az-text-small mt-3">
          * Este agrupamento não permite visualização da rentabilidade e vol.
          específicos.
        </small>
      </div>
    </FeedbackWrapper>
  </div>
</template>

<style lang="sass">
.detalhamento-alocacao-estrategica
  padding-bottom: 32px

  &--loading
    padding: 0 !important

  &--content-only
    width: 100%
    padding-bottom: 0

  &__header
    display: flex
    align-items: flex-start
    justify-content: space-between
    margin-bottom: 32px

  &--content-only &__header
    justify-content: flex-end
    margin-top: 24px

  &__info
    display: flex
    gap: 24px

    &-perfil,
    &-status,
    &-risco,
    &-expandir
      display: flex
      gap: 4px
      align-items: center

  &__table
    tr
      td:first-child,
      th:first-child
        padding: 0 !important
        width: 5px !important
        height: 36px !important

        > div
          height: 100% !important
          width: 100% !important
</style>
